import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  runInInjectionContext,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { startWith, Subscription } from 'rxjs';
import {
  EWidgetRequest,
  EWidgetRequestCategory,
  idBasedWidgetRequests,
  widgetRequestsCategorized,
} from 'scw-digital-boards-widgets';
import { TReducerState } from '../../../../../app.config';
import { DynamicObservable } from '../../../../../core/classes/dynamic-observable';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { ELoadStatus } from '../../../../../state/state.interface';
import { EWidgetRequestResultType } from '../../../../settings/data-integrations/widget-requests/widget-requests-add-edit-dialog/widget-requests-add-edit-dialog.interface';
import { ETypeDependentInputs } from '../../widget-add-edit-dialog.interface';
import { WidgetRequestDropdownStore } from './widget-request-dropdown.store';

export interface IRatioWidgetFirstSelection {
  inputType: ETypeDependentInputs | null;
  widgetRequest: EWidgetRequest | null | number;
  widgetRequestCategory: EWidgetRequestCategory | null;
}

@Component({
  imports: [MatDropdown],
  providers: [WidgetRequestDropdownStore],
  selector: 'widget-request-dropdown',
  templateUrl: './widget-request-dropdown.component.html',
})
export class WidgetRequestDropdownComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly globalStore = inject<Store<TReducerState>>(Store<TReducerState>);
  private readonly injector = inject(Injector);
  public readonly store = inject(WidgetRequestDropdownStore);

  public readonly formControl = input.required<FormControl<IDropdownOption<EWidgetRequest | number>[]>>();
  public readonly widgetRequestCategoryFormControl =
    input.required<FormControl<IDropdownOption<EWidgetRequestCategory>[]>>();
  public readonly isFormSubmitted = input.required<boolean>();
  public readonly dropdownLabel = input.required<string>();
  public readonly inputType = input<ETypeDependentInputs>();
  public readonly resultType = input.required<EWidgetRequestResultType>();

  private readonly selectedWidgetRequestCategoryObs = new DynamicObservable(
    [] as IDropdownOption<EWidgetRequestCategory>[],
  );
  private readonly selectedWidgetRequestCategory = toSignal(this.selectedWidgetRequestCategoryObs.obs$, {
    initialValue: [],
  });

  public readonly dropdownOptions: Signal<IDropdownOption<number | EWidgetRequest>[]> = computed(() => {
    const selectedWidgetRequestCategory = this.selectedWidgetRequestCategory();

    if (!selectedWidgetRequestCategory?.length) {
      return [];
    }

    const [{ id: category }] = selectedWidgetRequestCategory;

    if (category === EWidgetRequestCategory.dataSource) {
      return this.store.widgetRequests().map(({ id, name }) => ({ id, name }));
    }

    if (category === EWidgetRequestCategory.formSubmissions) {
      return this.store.forms().map(({ id, name }) => ({ id, name }));
    }

    return widgetRequestsCategorized[category].map((name) => ({
      id: name,
      name: this.translate.instant(`widget.bigNumber.widgetRequestNames.${name}`),
    }));
  });

  private readonly fixedDropdownConfiguration = {
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
  };

  public dropdownConfiguration: Signal<IDropdownSettings> = computed(() => {
    return {
      ...this.fixedDropdownConfiguration,
      enableSearchFilter: false,
      text: '',
    };
  });

  private readonly dashboard = this.globalStore.selectSignal(({ dashboardContent }) => dashboardContent.dashboardData);

  public readonly areOptionsLoading = computed(() => {
    try {
      switch (this.selectedWidgetRequestCategory()[0]?.id) {
        case EWidgetRequestCategory.dataSource:
          return this.store.widgetRequestsAreLoading();
        case EWidgetRequestCategory.formSubmissions:
          return this.store.formsAreLoading();
        case EWidgetRequestCategory.production:
          return false;
      }
    } catch (_) {
      return false;
    }
  });

  private readonly subscriptions: Subscription[] = [];

  constructor() {
    effect(() => {
      try {
        this.store.loadCustomWidgetRequests({
          filters: [
            {
              field: 'workspaceAuthorization',
              ids: [this.dashboard()!.workspace.id],
              operator: '$in',
            },
            {
              field: 'dashboardAuthorization',
              ids: [this.dashboard()!.id],
              operator: '$in',
            },
            {
              field: 'resultType',
              ids: [this.resultType()],
            },
          ],
        });
      } catch {}
    });

    this.store.loadForms({
      filters: [
        {
          field: 'isActive',
          ids: [true],
        },
      ],
    });
  }

  public ngOnInit(): void {
    this.selectedWidgetRequestCategoryObs.switch(
      this.widgetRequestCategoryFormControl().valueChanges.pipe(
        startWith(this.widgetRequestCategoryFormControl().value),
      ),
    );

    runInInjectionContext(this.injector, () => {
      const widgetRequestCategoryValue = toSignal(this.widgetRequestCategoryFormControl().valueChanges, {
        initialValue: this.widgetRequestCategoryFormControl().value,
      });

      this.unselectObsoleteOptionAndAutoselectSingleOption(widgetRequestCategoryValue);

      this.dropdownConfiguration = computed(() => {
        return {
          ...this.fixedDropdownConfiguration,
          enableSearchFilter: (idBasedWidgetRequests as readonly EWidgetRequestCategory[]).includes(
            widgetRequestCategoryValue()[0]?.id,
          ),
          text: this.dropdownLabel(),
        };
      });
    });
  }

  private unselectObsoleteOptionAndAutoselectSingleOption(
    widgetRequestCategoryValue: Signal<IDropdownOption<EWidgetRequestCategory>[]>,
  ): void {
    effect(() => {
      const dropdownOptions = this.dropdownOptions();
      const selectedRequest = this.formControl().value[0]?.id;
      const widgetRequestCategory = widgetRequestCategoryValue()[0]?.id;

      if (
        typeof selectedRequest === 'number' &&
        ((widgetRequestCategory === EWidgetRequestCategory.dataSource &&
          this.store.widgetRequestsLoadStatus() !== ELoadStatus.success) ||
          (widgetRequestCategory === EWidgetRequestCategory.formSubmissions &&
            this.store.formsLoadStatus() !== ELoadStatus.success))
      ) {
        return;
      }

      if (selectedRequest && !dropdownOptions.some(({ id }) => id === selectedRequest)) {
        this.formControl().reset([]);

        return;
      }

      if (dropdownOptions.length === 1) {
        this.formControl().reset([dropdownOptions[0]]);
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
